/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils\providerTemplates.tier1.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Calls Routing module.
────────────────────────────────────────────────────────────────*/


import type { ProviderTemplate } from './providerTemplates';

export const TIER1_PROVIDER_TEMPLATES: ProviderTemplate[] = [
    // Telnyx
    {
        id: 'telnyx',
        name: 'Telnyx',
        description: 'Enterprise-grade SIP trunking with global coverage',
        website: 'https://telnyx.com',
        fields: [
            { name: 'username', label: 'SIP Username', type: 'text', required: true, placeholder: 'your-sip-username' },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.telnyx.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Supports T.38 fax. Get credentials from Telnyx Mission Control Portal.'
    },

    // Twilio
    {
        id: 'twilio',
        name: 'Twilio',
        description: 'Elastic SIP Trunking with global reach',
        website: 'https://twilio.com',
        fields: [
            { name: 'username', label: 'Trunk SID', type: 'text', required: true, placeholder: 'TKxxxxxxxxxxxxxxxx' },
            { name: 'password', label: 'Auth Token', type: 'password', required: true },
            {
                name: 'region', label: 'Region', type: 'select', required: true,
                options: [
                    { value: 'us1', label: 'US - Virginia' }, { value: 'us2', label: 'US - Oregon' },
                    { value: 'ie1', label: 'Ireland' }, { value: 'de1', label: 'Germany' },
                    { value: 'au1', label: 'Australia' }, { value: 'jp1', label: 'Japan' },
                    { value: 'sg1', label: 'Singapore' }, { value: 'br1', label: 'Brazil' }
                ]
            }
        ],
        defaults: { proxy: '{region}.pstn.twilio.com', register: false, registerTransport: 'tls', expireSeconds: 3600, retrySeconds: 30, context: 'public' },
        notes: 'Twilio uses IP authentication. Configure your PBX IP in Twilio Console.'
    },

    // Signalwire
    {
        id: 'signalwire',
        name: 'Signalwire',
        description: 'Developer-friendly cloud communications',
        website: 'https://signalwire.com',
        fields: [
            { name: 'spaceUrl', label: 'Space URL', type: 'text', required: true, placeholder: 'your-space.signalwire.com', helpText: 'Your Signalwire space (without https://)' },
            { name: 'username', label: 'Project ID', type: 'text', required: true, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
            { name: 'password', label: 'API Token', type: 'password', required: true }
        ],
        defaults: { proxy: '{spaceUrl}', register: true, registerTransport: 'tcp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Get credentials from Signalwire Dashboard → Project Settings → API.'
    },

    // Voip.ms
    {
        id: 'voipms',
        name: 'Voip.ms',
        description: 'Wholesale VoIP provider for US/Canada',
        website: 'https://voip.ms',
        fields: [
            { name: 'username', label: 'Main Account', type: 'text', required: true, placeholder: '100000' },
            { name: 'subAccount', label: 'Sub Account', type: 'text', required: false, placeholder: '100000_subaccount' },
            { name: 'password', label: 'SIP Password', type: 'password', required: true },
            {
                name: 'server', label: 'Server', type: 'select', required: true,
                options: [
                    { value: 'atlanta', label: 'Atlanta' }, { value: 'chicago', label: 'Chicago' },
                    { value: 'dallas', label: 'Dallas' }, { value: 'denver', label: 'Denver' },
                    { value: 'losangeles', label: 'Los Angeles' }, { value: 'newyork', label: 'New York' },
                    { value: 'seattle', label: 'Seattle' }, { value: 'toronto', label: 'Toronto' },
                    { value: 'montreal', label: 'Montreal' }, { value: 'vancouver', label: 'Vancouver' }
                ]
            }
        ],
        defaults: { proxy: '{server}.voip.ms', register: true, registerTransport: 'udp', expireSeconds: 300, retrySeconds: 30, context: 'public' },
        notes: 'Create sub-accounts in Voip.ms portal for each PBX.'
    },

    // Flowroute
    {
        id: 'flowroute',
        name: 'Flowroute',
        description: 'Developer-friendly SIP trunking',
        website: 'https://flowroute.com',
        fields: [
            { name: 'username', label: 'Access Key', type: 'text', required: true },
            { name: 'password', label: 'Secret Key', type: 'password', required: true },
            {
                name: 'region', label: 'Region', type: 'select', required: true,
                options: [
                    { value: 'us-west-or', label: 'US West (Oregon)' }, { value: 'us-west-sjc', label: 'US West (San Jose)' },
                    { value: 'us-east-nj', label: 'US East (New Jersey)' }, { value: 'us-east-va', label: 'US East (Virginia)' }
                ]
            }
        ],
        defaults: { proxy: '{region}.sip.flowroute.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' }
    },

    // Bandwidth
    {
        id: 'bandwidth',
        name: 'Bandwidth',
        description: 'Enterprise communications platform',
        website: 'https://bandwidth.com',
        fields: [
            { name: 'username', label: 'SIP Username', type: 'text', required: true },
            { name: 'password', label: 'SIP Password', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.bandwidth.com', register: true, registerTransport: 'udp', expireSeconds: 600, retrySeconds: 30, context: 'public' },
        notes: 'Configure SIP credentials in Bandwidth Dashboard → Voice → Applications.'
    },

    // Plivo
    {
        id: 'plivo',
        name: 'Plivo',
        description: 'API-first cloud communications',
        website: 'https://plivo.com',
        fields: [
            { name: 'username', label: 'Auth ID', type: 'text', required: true },
            { name: 'password', label: 'Auth Token', type: 'password', required: true }
        ],
        defaults: { proxy: 'sip.plivo.com', register: false, registerTransport: 'udp', expireSeconds: 3600, retrySeconds: 30, context: 'public' },
        notes: 'Plivo uses IP-based authentication. Add your PBX IP in Plivo Console.'
    },
];
